import { computed, onBeforeUnmount, toValue } from "vue";
import { arrow, autoUpdate, flip, offset, shift, size, useFloating } from "@floating-ui/vue";
import { resolveLengthToPx } from "#ce/utils/css";

// Map the legacy `location` / `horizontalAlign` props onto a Floating UI
// placement. A bare side (`top`/`bottom`) centres the popup on that side; only
// the `-start` / `-end` variants align to the trigger's start/end edge.
export function toPlacement({ location, horizontalAlign } = {}) {
  if (location === "top") {
    return "top";
  }
  if (location === "bottom-left") {
    return "bottom-start";
  }
  if (location === "bottom-right") {
    return "bottom-end";
  }
  if (horizontalAlign === "left") {
    return "bottom-start";
  }
  if (horizontalAlign === "right") {
    return "bottom-end";
  }
  return "bottom";
}

// Below this much available space the popup flips to the other side instead of
// squeezing further. Keeps the preferred (bottom) placement whenever a usable
// amount of the list fits, matching the old in-DOM popup's open-downward bias.
const MIN_POPUP_HEIGHT = 220;

// Default gap between the activator and the popup (px). Tight by default so the
// app-wide selects stay snug; consumers that want a roomier gap (the doctor
// finder search/filters) set `--sp-comp-popup-offset` on a light-DOM ancestor —
// it inherits into this shadow tree and is read per-update below.
const DEFAULT_OFFSET = 4;
const DEFAULT_POINTER_OFFSET = 10;

const FLIP_PROPERTY = "--sp-comp-popup-flip";

/**
 * Whether the popup may move to the opposite side when its own side runs out of room. Hosts opt out
 * with `--sp-comp-popup-flip: false` on a light-DOM ancestor, which inherits into this shadow tree.
 * A locked popup still caps to `MIN_POPUP_HEIGHT` and overflows the viewport past that.
 */
function allowsFlip(floating) {
  return getComputedStyle(floating).getPropertyValue(FLIP_PROPERTY).trim().toLowerCase() !== "false";
}

/**
 * Floating UI positioning for the popup: places and sizes it against its anchor.
 * Separate from the visibility path — Floating UI writes inline styles, so it
 * runs the same whether the popup is shown via the native Popover API or the
 * no-API fallback.
 *
 * @param {object} options
 * @param {import("vue").Ref<HTMLElement|null>} options.popup Floating element ref.
 * @param {import("vue").Ref<HTMLElement|null>} options.arrowEl Arrow element ref.
 * @param {() => HTMLElement | { getBoundingClientRect: () => DOMRect, contextElement?: Element } | null} options.anchor
 *   Anchor getter — resolves the reference (HTML or virtual) element, or null.
 * @param {() => string} options.location `location` prop getter.
 * @param {() => string} options.horizontalAlign `horizontalAlign` prop getter.
 * @param {() => boolean} options.hasPointer Whether the arrow pointer is rendered.
 * @param {() => boolean} options.enabled Floating (popover) variant — false for permanent.
 * @param {import("vue").Ref<boolean>} options.isActive Open state — Floating UI's `open` input.
 * @returns {{ popupStyle: import("vue").ComputedRef, arrowStyle: import("vue").ComputedRef, isLocationTop: import("vue").ComputedRef, startAutoUpdate: () => void, stopAutoUpdate: () => void }}
 */
export function usePopupPlacement({
  popup,
  arrowEl,
  anchor,
  location,
  horizontalAlign,
  hasPointer,
  enabled,
  isActive,
}) {
  const anchorRef = computed(() => toValue(anchor) ?? null);

  const placement = computed(() =>
    toPlacement({ location: toValue(location), horizontalAlign: toValue(horizontalAlign) }),
  );

  /**
   * Floating UI positioning pipeline (offset → size → flip → shift → arrow) that
   * places and sizes the popup against its anchor. Separate from the visibility
   * path: Floating UI writes inline styles, so it runs the same whether the popup
   * is shown via the native Popover API or the no-API fallback.
   */
  const middleware = computed(() => {
    const list = [
      offset(({ elements }) => {
        const token = getComputedStyle(elements.floating).getPropertyValue("--sp-comp-popup-offset");
        const resolved = resolveLengthToPx(token, elements.floating);
        if (resolved != null) {
          return resolved;
        }
        return toValue(hasPointer) ? DEFAULT_POINTER_OFFSET : DEFAULT_OFFSET;
      }),
      // size() runs BEFORE flip() on purpose: it caps the popup to the space on
      // the preferred side (with a floor), so flip() measures the capped element
      // and only flips when even MIN_POPUP_HEIGHT doesn't fit. With flip() first,
      // its bestFit fallback compares the *uncapped* content height against both
      // sides and effectively coin-flips tall lists to the top.
      size({
        padding: 8,
        apply({ availableHeight, rects, elements }) {
          if (!toValue(enabled)) {
            return;
          }
          // `--sp-comp-popup-width/min-width: 100%` meant "as wide as the trigger"
          // under the old in-DOM positioning. In the top layer 100% resolves to the
          // viewport, so translate those (and only those) to the anchor's width.
          const refWidth = `${Math.round(rects.reference.width)}px`;
          const computed = getComputedStyle(elements.floating);
          if (rects.reference.width && computed.getPropertyValue("--sp-comp-popup-width").trim() === "100%") {
            elements.floating.style.setProperty("width", refWidth, "important");
            // max-width beats width regardless of importance, so the stylesheet's
            // 60ch default cap would clamp anchors wider than 60ch of the font.
            elements.floating.style.setProperty("max-width", refWidth, "important");
          }
          if (rects.reference.width && computed.getPropertyValue("--sp-comp-popup-min-width").trim() === "100%") {
            elements.floating.style.setProperty("min-width", refWidth, "important");
          }
          // Cap height to the space Floating UI found and let the content scroll,
          // instead of overflowing off-screen. floor() — never round up: a cap a
          // sub-pixel above the true available space reads as overflow to flip()
          // and sends the popup to the wrong side.
          elements.floating.style.setProperty(
            "--sp-popup-available-height",
            `${Math.max(MIN_POPUP_HEIGHT, Math.floor(availableHeight))}px`,
          );
        },
      }),
      // Turning both axis checks off leaves flip() with no overflow to react to, so the popup keeps
      // the placement it was given. Read per update off the floating element, which is what carries
      // the token down from the host.
      flip((state) => {
        const locked = !allowsFlip(state.elements.floating);

        return { padding: 8, mainAxis: !locked, crossAxis: !locked };
      }),
      shift({ padding: 8 }),
    ];

    if (toValue(hasPointer)) {
      list.push(arrow({ element: arrowEl, padding: 12 }));
    }

    return list;
  });

  const {
    floatingStyles,
    middlewareData,
    placement: resolvedPlacement,
    update,
    isPositioned,
  } = useFloating(anchorRef, popup, {
    strategy: "fixed",
    placement,
    middleware,
    open: isActive,
  });

  // SpPopup keeps the floating element mounted and toggles visibility via the
  // Popover API / CSS `display`, so `whileElementsMounted: autoUpdate` would keep
  // scroll/resize/resizeObserver listeners running on every mounted popup even
  // while closed. Drive autoUpdate manually instead — started on show, stopped on
  // hide — so listeners exist only while the popup is visible.
  let cleanupAutoUpdate = null;

  function startAutoUpdate() {
    if (!toValue(enabled)) {
      return;
    }
    const referenceEl = toValue(anchorRef);
    const floatingEl = popup.value;
    if (!referenceEl || !floatingEl) {
      return;
    }
    stopAutoUpdate();
    cleanupAutoUpdate = autoUpdate(referenceEl, floatingEl, update);
    update();
  }

  function stopAutoUpdate() {
    if (cleanupAutoUpdate) {
      cleanupAutoUpdate();
      cleanupAutoUpdate = null;
    }
  }

  onBeforeUnmount(stopAutoUpdate);

  // Only the floating (popover) variant takes Floating UI's inline position;
  // permanent stays in normal flow.
  const popupStyle = computed(() => (toValue(enabled) ? floatingStyles.value : {}));

  const isLocationTop = computed(() => resolvedPlacement.value?.startsWith("top"));

  // Position the optional arrow against the anchor (tracks flip/shift).
  const arrowStyle = computed(() => {
    const data = middlewareData.value.arrow;
    if (!data) {
      return {};
    }
    const style = {};
    if (data.x != null) {
      style.left = `${data.x}px`;
    }
    if (data.y != null) {
      style.top = `${data.y}px`;
    }
    return style;
  });

  return { popupStyle, arrowStyle, isLocationTop, isPositioned, startAutoUpdate, stopAutoUpdate };
}
